import { type SurveyDefinitionByUserResponse } from '~/api'
import { type ServerResponse, axios } from '~/axios'
import { type CurrentSurvey } from '~/utils/types/current-survey'

export async function getSurveysDefinition() {
  const query = /* GraphQL */ `
    query {
      surveysDefinitionByUser {
        id
        title
        createdAt
        description
        indicatorsCount
      }
    }
  `

  return await axios<
    ServerResponse<{
      surveysDefinitionByUser: SurveyDefinitionByUserResponse[]
    }>
  >({
    method: 'POST',
    url: '/graphql',
    data: { query },
  })
}

export async function getSurveyById(surveyId: number) {
  const query = /* GraphQL */ `
    query GetSurveyById($surveyId: Long!) {
      surveyById(surveyId: $surveyId) {
        customLabels {
          labelText
          labelType
        }
        lifemapQuestions {
          questionText
          questionType
        }
        title
        id
        createdAt
        status
        description
        minimumPriorities
        lang
        stoplightType
        privacyPolicy {
          title
          text
        }
        termsConditions {
          title
          text
        }
        surveyConfig {
          disableMemberQuestions
          familyCodeQRSupport
          verifiedEmailRequired
          isDemo
          isSurveyAnonymous
          shouldCreateUserBeforeSurveyFlow
          documentType {
            text
            value
            otherOption
          }
          gender {
            text
            value
            otherOption
          }
          stoplightOptional
          signSupport
          pictureSupport
          surveyLocation {
            country
            latitude
            longitude
          }
        }
        surveyEconomicQuestions {
          questionText
          codeName
          answerType
          shortName
          topic
          topicAudio
          required
          forFamilyMember
          orderNumber
          introduction
          options {
            text
            value
            otherOption
            conditions {
              codeName
              type
              values
              operator
              valueType
              showIfNoData
            }
          }
          conditions {
            codeName
            type
            value
            operator
          }
          conditionGroups {
            groupOperator
            joinNextGroup
            conditions {
              codeName
              type
              value
              operator
            }
          }
        }
        surveyStoplightQuestions {
          questionText
          definition
          shortName
          description
          codeName
          dimension
          id
          surveyStoplightDimension {
            id
          }
          surveyIndicator {
            codeName
          }
          questionAudio
          stoplightColors {
            url
            urlImage
            value
            description
          }
          required
          orderNumber
          reviewed
          status
          enableDoesNotApply
        }
        disclaimer {
          title
          subtitle
          text
        }
      }
    }
  `

  return await axios<ServerResponse<{ surveyById: CurrentSurvey }>>({
    method: 'post',
    url: '/graphql',
    data: { query, variables: { surveyId } },
  })
}

export async function assignUsersToSurvey({
  userIdsToAssign,
  surveyDefinitionId,
}: {
  userIdsToAssign: number[]
  surveyDefinitionId: number
}) {
  const query = /* GraphQL */ `
    mutation assignUsersToSurveyDefinition(
      $users: [Long]
      $surveyDefinition: Long
    ) {
      assignUsersToSurveyDefinition(
        users: $users
        surveyDefinition: $surveyDefinition
      ) {
        successful
      }
    }
  `

  return await axios<
    ServerResponse<{ assignUsersToSurveyDefinition: { successful: boolean } }>
  >({
    method: 'post',
    url: '/graphql',
    data: {
      query,
      variables: {
        users: userIdsToAssign,
        surveyDefinition: surveyDefinitionId,
      },
    },
  })
}
